import { useState } from 'react';
import { Puzzle, Zap, AlertCircle, Code, ChevronUp, ChevronDown, RefreshCw } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/common/Card';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { useActions, usePreApprovals, useCreatePreApproval, useDeletePreApproval } from '@/hooks/useActions';
import { usePlugins, useEnablePlugin, useDisablePlugin, useReloadPlugins } from '@/hooks/usePlugins';
import PluginsSection from '@/components/settings/PluginsSection';

const ACTION_GROUPS = [
  {
    id: 'classification', name: 'Email Classification', description: 'Categorize and flag emails',
    actions: ['mark_read', 'set_category', 'flag_email']
  },
  {
    id: 'management', name: 'Email Management', description: 'Delete, archive, and move emails',
    actions: ['delete_email', 'archive_email', 'move_to_folder']
  },
  {
    id: 'rules', name: 'Email Rules', description: 'Create and manage filtering rules',
    actions: ['create_email_rule', 'edit_email_rule', 'delete_email_rule', 'get_email_rules']
  },
  {
    id: 'replies', name: 'Pending Replies', description: 'Track emails requiring responses',
    actions: ['register_pending_reply', 'delete_pending_reply', 'get_pending_replies', 'draft_reply']
  },
  {
    id: 'events', name: 'Calendar Events', description: 'Create and manage calendar events',
    actions: ['create_event', 'update_event', 'delete_event', 'get_upcoming_events']
  },
  {
    id: 'ai_instructions', name: 'AI Instructions', description: 'Manage AI behavior instructions',
    actions: ['add_ai_instruction', 'edit_ai_instruction', 'delete_ai_instruction', 'get_ai_instructions']
  },
  {
    id: 'search', name: 'Search & Read', description: 'Search and retrieve email content',
    actions: ['search_emails', 'read_email']
  },
  {
    id: 'other', name: 'Other Actions', description: 'Miscellaneous actions',
    actions: ['get_historic_prices']
  }
];

function PluginCard({ plugin, onEnable, onDisable }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-lg">{plugin.name}</h3>
            <Badge variant="secondary">v{plugin.version}</Badge>
            {plugin.hasError && <Badge variant="destructive"><AlertCircle className="h-3 w-3 mr-1" />Error</Badge>}
          </div>
          <p className="text-sm text-muted-foreground mb-2">{plugin.description || 'No description available'}</p>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span>Source: {plugin.source || 'unknown'}</span>
            {plugin.actionCount !== undefined && <span>{plugin.actionCount} action(s)</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={plugin.enabled ? 'destructive' : 'default'} size="sm"
            onClick={() => plugin.enabled ? onDisable(plugin.name) : onEnable(plugin.name)}>
            {plugin.enabled ? 'Disable' : 'Enable'}
          </Button>
          {plugin.actions?.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          )}
        </div>
      </div>
      {expanded && plugin.actions?.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center gap-2 mb-3"><Code className="h-4 w-4" /><span className="text-sm font-medium">Actions provided by this plugin:</span></div>
          <div className="space-y-2">
            {plugin.actions.map((action, idx) => (
              <div key={idx} className="bg-muted/50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-sm font-medium">{action.name}</span>
                  {action.category && <Badge variant="outline">{action.category}</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">{action.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {plugin.hasError && plugin.error && (
        <div className="mt-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-destructive">Plugin Error</p>
              <p className="text-xs text-destructive/80 mt-1">{plugin.error}</p>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function PluginListSection() {
  const { data: plugins, isLoading } = usePlugins();
  const enablePlugin = useEnablePlugin();
  const disablePlugin = useDisablePlugin();
  const reloadPlugins = useReloadPlugins();

  const enabledCount = plugins?.filter(p => p.enabled).length || 0;
  const totalCount = plugins?.length || 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Puzzle className="h-5 w-5" />Installed Plugins</CardTitle>
            <CardDescription>Enable, disable, and manage plugins</CardDescription>
          </div>
          <Button onClick={() => reloadPlugins.mutate()} disabled={reloadPlugins.isPending}
            className="flex items-center gap-2">
            <RefreshCw className={`h-4 w-4 ${reloadPlugins.isPending ? 'animate-spin' : ''}`} />Reload
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4 mb-4 text-sm">
          <div className="flex items-center gap-2"><span className="text-muted-foreground">Total:</span><Badge variant="secondary">{totalCount}</Badge></div>
          <div className="flex items-center gap-2"><span className="text-muted-foreground">Active:</span><Badge variant="success">{enabledCount}</Badge></div>
        </div>
        {isLoading ? <LoadingSpinner /> : plugins && plugins.length > 0 ? (
          <div className="space-y-4">
            {plugins.map(plugin => (
              <PluginCard key={plugin.name} plugin={plugin}
                onEnable={(name) => enablePlugin.mutate(name)} onDisable={(name) => disablePlugin.mutate(name)} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 border rounded-lg">
            <p className="text-muted-foreground mb-4">No plugins installed yet.</p>
            <p className="text-sm text-muted-foreground">Install plugins by adding them to your configuration or packages directory.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ActionApprovals() {
  const { data: actions, isLoading: actionsLoading, error: actionsError } = useActions(true);
  const { data: preApprovals, isLoading: preApprovalsLoading } = usePreApprovals('user@example.com');
  const createPreApproval = useCreatePreApproval();
  const deletePreApproval = useDeletePreApproval();

  const approvalMap = new Map();
  if (preApprovals && Array.isArray(preApprovals)) {
    preApprovals.forEach(approval => {
      approvalMap.set(approval.action_name, { id: approval.id, autoApprove: approval.auto_approve });
    });
  }

  const handleApprovalChange = async (actionName, newState) => {
    const existing = approvalMap.get(actionName);
    try {
      if (newState === 'always') {
        await createPreApproval.mutateAsync({ actionName, userEmail: 'user@example.com', autoApprove: true });
      } else if (newState === 'never') {
        await createPreApproval.mutateAsync({ actionName, userEmail: 'user@example.com', autoApprove: false });
      } else if (newState === 'ask' && existing?.id) {
        await deletePreApproval.mutateAsync(existing.id);
      }
    } catch (error) {
      console.error('Failed to update approval preference:', error);
    }
  };

  const isLoading = actionsLoading || preApprovalsLoading;
  const isProcessing = createPreApproval.isPending || deletePreApproval.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Zap className="h-5 w-5" />Action Approvals</CardTitle>
        <CardDescription>Configure approval preferences for AI-suggested actions</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? <LoadingSpinner /> : actionsError ? (
          <div className="text-center py-8"><AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" /><p className="text-sm text-red-600">Failed to load actions</p></div>
        ) : !actions || actions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">No actions registered</div>
        ) : (
          <div className="space-y-3">
            {ACTION_GROUPS.map(group => (
              <ActionGroup key={group.id} group={group} actions={actions} approvalMap={approvalMap}
                onApprovalChange={handleApprovalChange} isProcessing={isProcessing} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ActionGroup({ group, actions, approvalMap, onApprovalChange, isProcessing }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const groupActions = actions.filter(action => group.actions.includes(action.name));
  if (groupActions.length === 0) return null;

  const enabledActions = groupActions.filter(a => a.enabled);
  const states = enabledActions.map(action => {
    const approval = approvalMap.get(action.name);
    if (!approval) return 'ask';
    return approval.autoApprove ? 'always' : 'never';
  });

  const allSame = states.length > 0 && states.every(s => s === states[0]);
  const groupState = allSame ? states[0] : 'mixed';

  const handleBulkApprove = () => {
    enabledActions.forEach(action => onApprovalChange(action.name, 'always'));
  };

  const handleBulkAsk = () => {
    enabledActions.forEach(action => onApprovalChange(action.name, 'ask'));
  };

  const handleBulkDeny = () => {
    enabledActions.forEach(action => onApprovalChange(action.name, 'never'));
  };

  return (
    <div className="border rounded-lg">
      <div className="p-4 bg-muted/30">
        <div className="flex items-center justify-between">
          <button onClick={() => setIsExpanded(!isExpanded)} className="flex items-center gap-2 flex-1 text-left hover:opacity-80 transition-opacity">
            {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
            <div>
              <h3 className="font-medium text-sm">{group.name}</h3>
              <p className="text-xs text-muted-foreground">{group.description}</p>
            </div>
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground mr-2">{enabledActions.length} action{enabledActions.length !== 1 ? 's' : ''}</span>
            <div className="flex gap-1">
              <button onClick={handleBulkApprove} disabled={isProcessing || enabledActions.length === 0}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors disabled:opacity-50 ${groupState === 'always' ? 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'}`}
                title="Approve all in group"><Check className="h-3 w-3" /></button>
              <button onClick={handleBulkAsk} disabled={isProcessing || enabledActions.length === 0}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors disabled:opacity-50 ${groupState === 'ask' ? 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'}`}
                title="Ask for all in group">?</button>
              <button onClick={handleBulkDeny} disabled={isProcessing || enabledActions.length === 0}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors disabled:opacity-50 ${groupState === 'never' ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'}`}
                title="Deny all in group"><X className="h-3 w-3" /></button>
            </div>
          </div>
        </div>
      </div>
      {isExpanded && (
        <div className="p-4 space-y-2">
          {groupActions.map(action => {
            const approval = approvalMap.get(action.name);
            const currentState = !approval ? 'ask' : approval.autoApprove ? 'always' : 'never';
            return (
              <div key={action.name} className="pl-6 py-2 border-l-2 border-muted">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-xs">{action.displayName || action.name}</h4>
                      <Badge variant="outline" className="text-xs">{action.source}</Badge>
                      {action.scope && <Badge variant="secondary" className="text-xs">{action.scope}</Badge>}
                      {!action.enabled && <Badge variant="secondary" className="text-xs">disabled</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">{action.description}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => onApprovalChange(action.name, 'always')} disabled={isProcessing || !action.enabled}
                      className={`px-2 py-1 text-xs font-medium rounded transition-colors disabled:opacity-50 ${currentState === 'always' ? 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'}`}
                      title="Always auto-approve"><Check className="h-3 w-3" /></button>
                    <button onClick={() => onApprovalChange(action.name, 'ask')} disabled={isProcessing || !action.enabled}
                      className={`px-2 py-1 text-xs font-medium rounded transition-colors disabled:opacity-50 ${currentState === 'ask' ? 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'}`}
                      title="Always ask">?</button>
                    <button onClick={() => onApprovalChange(action.name, 'never')} disabled={isProcessing || !action.enabled}
                      className={`px-2 py-1 text-xs font-medium rounded transition-colors disabled:opacity-50 ${currentState === 'never' ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'}`}
                      title="Always reject"><X className="h-3 w-3" /></button>
                  </div>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {currentState === 'always' && <span className="text-green-600 dark:text-green-400">Auto-approve</span>}
                  {currentState === 'ask' && <span className="text-blue-600 dark:text-blue-400">Always ask</span>}
                  {currentState === 'never' && <span className="text-red-600 dark:text-red-400">Always reject</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function SettingsPlugins() {
  return (
    <div className="space-y-6">
      <PluginsSection />
      <PluginListSection />
      <ActionApprovals />
    </div>
  );
}
